<script setup lang="ts">
import { computed, reactive } from 'vue'
import type { ScenarioChatMessageNodeData } from '../types'
import { Position, Handle } from '@vue-flow/core'
import IconCalendar from '@/components/icons/IconCalendar.vue'
import { assertExhaustive } from '@/libs/utilities/assertExhaustive'
import SelectionSourceHandle from './SelectionSourceHandle.vue'
import { useInternalScenarioChatFormStore } from '../form/form'

const props = defineProps<{
  data: ScenarioChatMessageNodeData
  selected: boolean
}>()

const formStore = reactive(useInternalScenarioChatFormStore())

const hasConnectionAsTarget = computed(() => {
  const value = formStore.messageHasConnectionAsTargetMap.get(
    props.data.message.clientOnlyId
  )
  if (value === undefined) {
    throw new Error('hasConnectionAsTarget is undefined')
  }

  return value
})

const height = computed(() => {
  switch (props.data.message.selections.type) {
    case 'BasicSelections':
      const selectionsLineLength =
        props.data.message.selections.basicSelections.length
      // prettier-ignore
      return (
        16 * 2 + // padding
        18 + // forUserIndex
        4 + // forUserIndex と body の間
        18 + // body
        8 + // body と選択肢の間
        selectionsLineLength * 32 + // 選択肢
        (selectionsLineLength - 1) * 8 // 選択肢間の余白
      )
    case 'BookingRequestSelections':
      // prettier-ignore
      return (
        16 * 2 + // padding
        18 + // forUserIndex
        4 + // forUserIndex と body の間
        18 + // body
        8 + // body と 日程調整 の間
        36 // 日程調整
      )
    case 'None':
      // prettier-ignore
      return (
        16 * 2 + // padding
        18 + // forUserIndex
        4 + // forUserIndex と body の間
        18 // body
      )
    default:
      assertExhaustive(props.data.message.selections)
  }
})
</script>

<template>
  <div
    :class="{
      [$style.container]: true,
      [$style['container--selected']]: selected,
    }"
    :style="{ height: `${height}px` }"
  >
    <p :class="$style.forUserIndex">{{ data.message.forUserIndex }}.</p>
    <div v-if="data.message.body.length > 0" :class="$style.body">
      {{ data.message.body }}
    </div>
    <div v-else :class="[$style.body, $style['body--empty']]">
      メッセージ本文を入力する...
    </div>
    <div
      :class="$style.basicSelections"
      v-if="data.message.selections.type === 'BasicSelections'"
    >
      <div
        v-for="selection in data.message.selections.basicSelections"
        :key="selection.clientOnlyId"
        :class="$style.basicSelection"
      >
        ・{{ selection.label }}
      </div>
    </div>
    <div
      v-if="data.message.selections.type === 'BookingRequestSelections'"
      :class="$style.bookingRequestSelections"
    >
      <IconCalendar :class="$style.bookingRequestSelectionsIcon" />
      <p>日程調整</p>
    </div>

    <div :class="$style.selectionSourceHandles">
      <div
        v-if="
          data.message.selections != undefined &&
          data.message.selections.type === 'BasicSelections'
        "
        v-for="(selection, index) in data.message.selections.basicSelections"
        :key="selection.clientOnlyId"
        :style="{
          position: 'absolute',
          top: 'auto',
          bottom: `${height - 85 - index * 40}px`,
          right: '0px',
        }"
      >
        <SelectionSourceHandle
          :message-client-only-id="data.message.clientOnlyId"
          :selection-item="selection"
          @add-next-message="
            (messageClientOnlyId: string, selectionClientOnlyId: string) => {
              formStore.addNextMessage({
                messageClientOnlyId,
                selectionClientOnlyId,
              })
            }
          "
        />
      </div>
    </div>

    <Handle
      type="target"
      :position="Position.Left"
      :class="{
        [$style.targetHandle]: true,
        [$style['targetHandle--hasConnection']]: hasConnectionAsTarget,
      }"
    />
  </div>
</template>

<style lang="scss" module>
.container {
  width: 240px;
  background-color: #ffffff;
  border: 2px solid transparent;
  border-radius: 8px;
  padding: 16px;
  box-sizing: border-box;
  box-shadow:
    0 1px 3px 0 rgb(0 0 0 / 10%),
    0 1px 2px -1px rgb(0 0 0 / 10%);
  position: relative;

  &--selected {
    border-color: $color-okaneko-blue;
  }

  &:hover {
    box-shadow:
      0 10px 15px -3px rgb(0 0 0 / 10%),
      0 4px 6px -4px rgb(0 0 0 / 10%);
  }
}

.selectionSourceHandles {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
}

.forUserIndex {
  color: $color-text-and-object-low-emphasis;
  font-size: 12px;
  margin-bottom: 4px;
}

.body {
  font-size: 12px;
  height: 1lh;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  &--empty {
    color: $color-text-and-object-low-emphasis;
  }
}

.basicSelections {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.basicSelection {
  background-color: #f0f0f0;
  font-size: 12px;
  padding: 0 4px;
  height: 32px;
  line-height: 32px;
  border-radius: 4px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.bookingRequestSelections {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: #f0f0f0;
  border-radius: 4px;
  padding: 8px;
}

.bookingRequestSelectionsIcon {
  font-size: 18px;
}

.targetHandle {
  /* stylelint-disable-next-line */
  &:global(.vue-flow__handle) {
    width: 8px;
    height: 24px;
    border-radius: 4px;
    background-color: $color-gray-500;
  }

  &--hasConnection {
    /* stylelint-disable selector-class-pattern */
    &:global(.vue-flow__handle) {
      background-color: $color-okaneko-blue;
    }
    /* stylelint-enable selector-class-pattern */
  }
}
</style>
